import type { Submission } from '@/types'
import { normalizeTaxonomySelection } from '../taxonomySelection'
import { toUtcIsoString } from '../time'
import { getAbsoluteEndpoint, getMeSubmissionsPath, getSubmissionsPath } from '../apiRouting'
import {
  ApiError,
  SubmitResponse,
  blobToBase64,
  buildProtectedAuthHeaders,
  fetchWithTimeout,
  readApiError,
  throwIfAuthRejected,
  throwIfSubmissionRejected,
} from './client'

export interface GetSubmissionsResponse {
  submissions: Submission[]
  count: number
  lastKey?: string
  hasMore?: boolean
}

function buildSubmissionsUrl(basePath: string, options?: {
  assetId?: string
  limit?: number
  lastKey?: string
}): string {
  const params = new URLSearchParams()
  if (options?.assetId) {
    params.append('assetId', options.assetId)
  }
  if (options?.limit) {
    params.append('limit', options.limit.toString())
  }
  if (options?.lastKey) {
    params.append('lastKey', options.lastKey)
  }
  return `${basePath}${params.toString() ? `?${params.toString()}` : ''}`
}

export async function submitTextSubmission(submission: Submission): Promise<SubmitResponse> {
  if (submission.type !== 'text') {
    throw new Error('Submission type must be "text"')
  }

  const taxonomySelection = normalizeTaxonomySelection(submission)
  const headers = await buildProtectedAuthHeaders('upload submissions', {
    'Content-Type': 'application/json',
    'Idempotency-Key': submission.id,
  })
  const submissionsUrl = getAbsoluteEndpoint('submissionsWrite', getSubmissionsPath)

  const response = await fetchWithTimeout(submissionsUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      id: submission.id,
      type: 'text',
      userId: submission.userId,
      assetId: taxonomySelection.assetId,
      indicationId: taxonomySelection.indicationId,
      kiqIds: taxonomySelection.kiqIds,
      insightTags: submission.insightTags ?? [],
      createdAt: toUtcIsoString(submission.createdAt),
      noteBody: submission.noteBody,
      tags: submission.tags,
    }),
  }, 45_000)

  if (!response.ok) {
    const error: ApiError = await readApiError(response)
    throwIfSubmissionRejected(response, error)
    throwIfAuthRejected(response, 'upload submissions')
    throw new Error(error.message || error.error || 'Failed to submit text submission')
  }

  return response.json()
}

export async function submitAudioSubmission(
  submission: Submission,
  audioBlob: Blob,
  mimeType: string = 'audio/webm',
): Promise<SubmitResponse> {
  if (submission.type !== 'audio') {
    throw new Error('Submission type must be "audio"')
  }

  const taxonomySelection = normalizeTaxonomySelection(submission)
  const audioData = await blobToBase64(audioBlob)

  const headers = await buildProtectedAuthHeaders('upload submissions', {
    'Content-Type': 'application/json',
    'Idempotency-Key': submission.id,
  })
  const submissionsUrl = getAbsoluteEndpoint('submissionsWrite', getSubmissionsPath)

  const response = await fetchWithTimeout(submissionsUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      id: submission.id,
      type: 'audio',
      userId: submission.userId,
      assetId: taxonomySelection.assetId,
      indicationId: taxonomySelection.indicationId,
      kiqIds: taxonomySelection.kiqIds,
      insightTags: submission.insightTags ?? [],
      createdAt: toUtcIsoString(submission.createdAt),
      audioData,
      audioMimeType: mimeType,
      durationMs: submission.durationMs,
      participants: submission.participants,
      recordingWithoutParticipants: submission.recordingWithoutParticipants,
      consentDescription: submission.consentDescription,
      consentConfirmed: submission.consentConfirmed,
    }),
  }, 180_000)

  if (!response.ok) {
    const error: ApiError = await readApiError(response)
    throwIfSubmissionRejected(response, error)
    throwIfAuthRejected(response, 'upload submissions')
    throw new Error(error.message || error.error || 'Failed to submit audio submission')
  }

  return response.json()
}

export async function getSubmissions(options?: {
  assetId?: string
  limit?: number
  lastKey?: string
}): Promise<GetSubmissionsResponse> {
  const headers = await buildProtectedAuthHeaders('load submissions', {
    'Content-Type': 'application/json',
  })

  const submissionsBase = getAbsoluteEndpoint('submissionsRead', getSubmissionsPath)
  const submissionsUrl = buildSubmissionsUrl(submissionsBase, options)

  const response = await fetchWithTimeout(submissionsUrl, {
    method: 'GET',
    headers,
  }, 45_000)

  if (!response.ok) {
    throwIfAuthRejected(response, 'load submissions')
    const error: ApiError = await readApiError(response)
    throw new Error(error.message || error.error || 'Failed to get submissions')
  }

  return response.json()
}

/**
 * Fetch the authenticated user's own submissions from the backend (DynamoDB),
 * newest first, page by page. Backs the "My Submissions" history view. The
 * backend scopes results to the caller's userId, so no user filter is sent.
 */
export async function getMySubmissions(options?: {
  limit?: number
  lastKey?: string
}): Promise<GetSubmissionsResponse> {
  const headers = await buildProtectedAuthHeaders('load my submissions', {
    'Content-Type': 'application/json',
  })

  const base = getAbsoluteEndpoint('submissionsRead', getMeSubmissionsPath)
  const params = new URLSearchParams()
  if (options?.limit) {
    params.append('limit', options.limit.toString())
  }
  if (options?.lastKey) {
    params.append('lastKey', options.lastKey)
  }
  const url = `${base}${params.toString() ? `?${params.toString()}` : ''}`

  const response = await fetchWithTimeout(url, {
    method: 'GET',
    headers,
  }, 45_000)

  if (!response.ok) {
    throwIfAuthRejected(response, 'load my submissions')
    const error: ApiError = await readApiError(response)
    throw new Error(error.message || error.error || 'Failed to get submissions')
  }

  return response.json()
}

export async function getSubmission(
  submissionId: string,
): Promise<{ processingStatus?: string; processingUpdatedAt?: string; processingError?: string }> {
  const headers = await buildProtectedAuthHeaders('get submission', {
    'Content-Type': 'application/json',
  })

  const submissionsBase = getAbsoluteEndpoint('submissionsRead', getSubmissionsPath)
  const response = await fetchWithTimeout(
    `${submissionsBase}/${encodeURIComponent(submissionId)}`,
    { method: 'GET', headers },
    15_000,
  )

  if (!response.ok) {
    throwIfAuthRejected(response, 'get submission')
    const error: ApiError = await readApiError(response)
    throw new Error(error.message || error.error || 'Failed to get submission')
  }

  return response.json()
}

/**
 * Replace a recording's transcript with the owner's correction. Online-only —
 * the transcript lives server-side. The backend gates this by the same
 * per-binder "show transcript to user" setting that controls visibility and
 * returns the updated submission.
 */
export async function updateTranscript(
  submissionId: string,
  transcript: string,
): Promise<Submission> {
  const headers = await buildProtectedAuthHeaders('update transcript', {
    'Content-Type': 'application/json',
  })

  const submissionsBase = getAbsoluteEndpoint('submissionsWrite', getSubmissionsPath)
  const response = await fetchWithTimeout(
    `${submissionsBase}/${encodeURIComponent(submissionId)}/transcript`,
    {
      method: 'PUT',
      headers,
      body: JSON.stringify({ transcript }),
    },
    45_000,
  )

  if (!response.ok) {
    throwIfAuthRejected(response, 'update transcript')
    const error: ApiError = await readApiError(response)
    throw new Error(error.message || error.error || 'Failed to update transcript')
  }

  return response.json()
}

/**
 * Replace a text submission's note body with the owner's edit. Online-only — the
 * server re-derives the stored tags from the new text (preserving Insight Type
 * selections) and returns the updated submission. Editing is rejected (403) once
 * the asset's active period has closed.
 */
export async function updateNoteBody(
  submissionId: string,
  noteBody: string,
): Promise<Submission> {
  const headers = await buildProtectedAuthHeaders('update note', {
    'Content-Type': 'application/json',
  })

  const submissionsBase = getAbsoluteEndpoint('submissionsWrite', getSubmissionsPath)
  const response = await fetchWithTimeout(
    `${submissionsBase}/${encodeURIComponent(submissionId)}/note`,
    {
      method: 'PUT',
      headers,
      body: JSON.stringify({ noteBody }),
    },
    45_000,
  )

  if (!response.ok) {
    throwIfAuthRejected(response, 'update note')
    const error: ApiError = await readApiError(response)
    throw new Error(error.message || error.error || 'Failed to update note')
  }

  return response.json()
}

/**
 * Request deletion of a recording. The server soft-deletes it (a retention grace
 * period, default 3 days) and returns the updated submission with its pending
 * deletion state; a background sweeper removes everything once the window
 * elapses. Online-only; gated server-side by the binder's allowDelete setting.
 */
export async function requestSubmissionDeletion(submissionId: string): Promise<Submission> {
  const headers = await buildProtectedAuthHeaders('delete submission', {
    'Content-Type': 'application/json',
  })

  const submissionsBase = getAbsoluteEndpoint('submissionsWrite', getSubmissionsPath)
  const response = await fetchWithTimeout(
    `${submissionsBase}/${encodeURIComponent(submissionId)}/deletion`,
    { method: 'POST', headers },
    45_000,
  )

  if (!response.ok) {
    throwIfAuthRejected(response, 'delete submission')
    const error: ApiError = await readApiError(response)
    throw new Error(error.message || error.error || 'Failed to delete submission')
  }

  return response.json()
}

/** Cancel a pending deletion, restoring the submission. */
export async function cancelSubmissionDeletion(submissionId: string): Promise<Submission> {
  const headers = await buildProtectedAuthHeaders('cancel deletion', {
    'Content-Type': 'application/json',
  })

  const submissionsBase = getAbsoluteEndpoint('submissionsWrite', getSubmissionsPath)
  const response = await fetchWithTimeout(
    `${submissionsBase}/${encodeURIComponent(submissionId)}/deletion`,
    { method: 'DELETE', headers },
    45_000,
  )

  if (!response.ok) {
    throwIfAuthRejected(response, 'cancel deletion')
    const error: ApiError = await readApiError(response)
    throw new Error(error.message || error.error || 'Failed to cancel deletion')
  }

  return response.json()
}

export async function getSubmissionAudioUrl(
  submissionId: string,
): Promise<{ url: string; expiresAt: string; contentType: string }> {
  const headers = await buildProtectedAuthHeaders('get audio url', {
    'Content-Type': 'application/json',
  })

  const submissionsBase = getAbsoluteEndpoint('submissionsRead', getSubmissionsPath)
  const response = await fetchWithTimeout(
    `${submissionsBase}/${encodeURIComponent(submissionId)}/audio-url`,
    { method: 'GET', headers },
    15_000,
  )

  if (!response.ok) {
    throwIfAuthRejected(response, 'get audio url')
    const error: ApiError = await readApiError(response)
    throw new Error(error.message || error.error || 'Failed to get audio URL')
  }

  return response.json()
}
